import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { MapPin, Compass, Send, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { loadGoogleMaps } from '../utils/googleMapsLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const ACCREDITATIONS = [
  'VIC LVL1', 'VIC LVL2', 'VIC TC',
  'QLD LVL1', 'QLD LVL2',
  'WA', 'VIC PROB', 'NT PILOT', 'NT ESCORT',
  '4WD SPECIFIC', 'DIESEL', 'PETROL', 'GAS',
];

const PilotsAvailable = () => {
  const { toast } = useToast();
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('');
  const [accredFilter, setAccredFilter] = useState([]); // multi-select array of codes
  const [clearanceFilters, setClearanceFilters] = useState({
    available_now_only: false,
    has_msic: false,
    has_federal_police_clearance: false,
    has_defence_clearance: false,
    has_turbines_access: false,
    has_mine_sites: false,
    has_port_access: false,
    has_railway_inductions: false,
    has_aviation_clearances: false,
    verified_steerer_only: false,
  });
  const [selected, setSelected] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [enquiry, setEnquiry] = useState({ name: '', email: '', phone: '', route: '', dates: '', message: '' });
  const [sending, setSending] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => { fetchPilots(); }, [stateFilter, accredFilter]);

  const toggleAccred = (code) =>
    setAccredFilter((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  useEffect(() => {
    loadGoogleMaps()
      .then(() => setMapLoaded(true))
      .catch((err) => console.error('Google Maps failed:', err));
  }, []);

  // Client-side filter based on clearance checkboxes (must be defined before the map effect that uses it)
  const filteredPilots = pilots.filter(p => {
    if (clearanceFilters.available_now_only && p.is_available_now === false) return false;
    if (clearanceFilters.has_msic && !p.has_msic) return false;
    if (clearanceFilters.has_federal_police_clearance && !p.has_federal_police_clearance) return false;
    if (clearanceFilters.has_defence_clearance && !p.has_defence_clearance) return false;
    if (clearanceFilters.has_turbines_access && !p.has_turbines_access) return false;
    if (clearanceFilters.has_mine_sites && !(p.mine_sites?.length > 0)) return false;
    if (clearanceFilters.has_port_access && !p.port_access_details) return false;
    if (clearanceFilters.has_railway_inductions && !p.railway_inductions) return false;
    if (clearanceFilters.has_aviation_clearances && !p.aviation_clearances) return false;
    if (clearanceFilters.verified_steerer_only && !p.steerer_verified) return false;
    return true;
  });

  const toggleClearance = (key) => setClearanceFilters(prev => ({ ...prev, [key]: !prev[key] }));
  const clearFilters = () => setClearanceFilters({
    available_now_only: false,
    has_msic: false, has_federal_police_clearance: false, has_defence_clearance: false,
    has_turbines_access: false, has_mine_sites: false, has_port_access: false,
    has_railway_inductions: false, has_aviation_clearances: false, verified_steerer_only: false,
  });
  const activeClearanceCount = Object.values(clearanceFilters).filter(Boolean).length;

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    if (!mapInstance.current) {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: -25.2744, lng: 133.7751 },
        zoom: 4,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
    }
    // Clear previous markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    filteredPilots.forEach(p => {
      if (p.base_lat && p.base_lng) {
        const marker = new window.google.maps.Marker({
          position: { lat: p.base_lat, lng: p.base_lng },
          map: mapInstance.current,
          title: `${p.pilot_code} — ${p.base_suburb}, ${p.base_state}`,
        });
        marker.addListener('click', () => setSelected(p));
        markersRef.current.push(marker);
      }
    });
  }, [mapLoaded, filteredPilots]);

  const fetchPilots = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stateFilter) params.set('state', stateFilter);
      if (accredFilter.length) params.set('accreditation', accredFilter.join(','));
      const res = await axios.get(`${API}/pilots/availability/public?${params.toString()}`);
      setPilots(res.data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const sendEnquiry = async () => {
    if (!selected) return;
    if (!enquiry.name || !enquiry.email) {
      toast({ title: 'Missing info', description: 'Please enter your name and email.', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/pilots/availability/${selected.id}/enquire`, enquiry);
      toast({ title: 'Enquiry sent', description: 'Truckn Send It will connect you with the pilot shortly.' });
      setSelected(null);
      setEnquiry({ name: '', email: '', phone: '', route: '', dates: '', message: '' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to send', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-[#1e3a5f] text-white py-8">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold flex items-center gap-2"><Compass className="w-8 h-8" /> Pilots Available</h1>
              <p className="text-gray-200 mt-1">Find escort &amp; steering pilots across Australia. Contact any listing through Truckn Send It — we&apos;ll relay your request.</p>
            </div>
            <Link
              to="/book-pilot"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-3 rounded-full shadow-lg transition-all hover:shadow-xl"
              data-testid="pilots-available-book-cta"
            >
              Book a Pilot / Steerer
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6">
        <Card className="p-4 mb-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label>Filter by State</Label>
              <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} className="w-full p-2 border rounded" data-testid="filter-state">
                <option value="">All states</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <div className="flex items-center justify-between">
                <Label>Filter by Accreditation (any of)</Label>
                {accredFilter.length > 0 && (
                  <button
                    onClick={() => setAccredFilter([])}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="clear-accred-filter"
                  >
                    Clear ({accredFilter.length})
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="filter-accred">
                {ACCREDITATIONS.map((a) => {
                  const active = accredFilter.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => toggleAccred(a)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        active
                          ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#1e3a5f]'
                      }`}
                      data-testid={`filter-accred-${a.replace(/\s+/g, '-')}`}
                    >
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-end">
              <p className="text-sm text-gray-600">{loading ? 'Loading...' : `${filteredPilots.length} of ${pilots.length} pilot${pilots.length === 1 ? '' : 's'} shown`}</p>
            </div>
          </div>

          {/* Clearance filter chips */}
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-[#1e3a5f]">Filter by Clearance / Access</p>
              {activeClearanceCount > 0 && (
                <button onClick={clearFilters} className="text-xs text-blue-600 hover:underline" data-testid="clear-clearance-filters">Clear all ({activeClearanceCount})</button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                ['available_now_only', '🟢 Available Now'],
                ['has_msic', 'MSIC'],
                ['has_federal_police_clearance', 'Federal Police'],
                ['has_defence_clearance', 'Defence Site'],
                ['has_turbines_access', 'Turbines'],
                ['has_mine_sites', 'Mine Site'],
                ['has_port_access', 'Port'],
                ['has_railway_inductions', 'Railway'],
                ['has_aviation_clearances', 'Aviation'],
                ['verified_steerer_only', '✓ Verified Steerer'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => toggleClearance(key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    clearanceFilters[key]
                      ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`clearance-filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </Card>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Card className="p-2">
              <div className="w-full h-[420px] rounded relative">
                <div ref={mapRef} className="absolute inset-0 rounded" data-testid="pilots-map"></div>
                {!mapLoaded && (
                  <p className="absolute inset-0 flex items-center justify-center text-gray-500 bg-gray-100 rounded">
                    Loading map...
                  </p>
                )}
              </div>
            </Card>
          </div>
          <div className="space-y-3 max-h-[440px] overflow-y-auto pr-2">
            {loading ? <p className="text-gray-600 text-sm">Loading pilots...</p> :
              filteredPilots.length === 0 ? <p className="text-gray-600 text-sm">No pilots match your filters.</p> :
              filteredPilots.map(p => (
                <Card
                  key={p.id}
                  className={`p-3 cursor-pointer transition-all ${selected?.id === p.id ? 'ring-2 ring-yellow-400' : 'hover:shadow-md'}`}
                  onClick={() => setSelected(p)}
                  data-testid={`pilot-card-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${p.is_available_now === false ? 'bg-gray-400' : 'bg-green-500'}`}
                        title={p.is_available_now === false ? 'Currently busy' : 'Available now'}
                      ></span>
                      <p className="font-semibold text-[#1e3a5f]">{p.pilot_code}</p>
                    </div>
                    {p.steerer_verified && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-800 rounded font-semibold" title="Verified steerer">✓ Verified Steerer</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3"/> {p.base_suburb}, {p.base_state}
                  </p>
                  {p.accreditations?.length > 0 && (
                    <p className="text-xs mt-1 text-gray-700">{p.accreditations.map(a => a.replace('_', ' ')).join(', ')}</p>
                  )}
                  {/* Additional clearance badges */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.has_msic && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">MSIC</span>}
                    {p.has_federal_police_clearance && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">Fed Police</span>}
                    {p.has_defence_clearance && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">Defence</span>}
                    {p.has_turbines_access && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">Turbines</span>}
                    {p.mine_sites?.length > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">Mine ({p.mine_sites.length})</span>}
                    {p.port_access_details && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">Port</span>}
                    {p.railway_inductions && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">Rail</span>}
                    {p.aviation_clearances && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">Aviation</span>}
                  </div>
                  {p.available_from && (
                    <p className="text-xs text-gray-500 mt-1">Available {p.available_from}{p.available_until ? ` → ${p.available_until}` : ''}</p>
                  )}
                </Card>
              ))
            }
          </div>
        </div>

        {selected && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg p-5 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-bold text-[#1e3a5f]">Contact {selected.pilot_code}</h3>
                  <p className="text-xs text-gray-500">{selected.base_suburb}, {selected.base_state}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5"/></button>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3 text-sm">
                <p><strong>Accreditations:</strong> {selected.accreditations?.map(a => a.replace('_', ' ')).join(', ') || '—'}</p>
                <p><strong>Services:</strong> {selected.services_offered?.join(', ') || '—'}{selected.steerer_verified && <span className="ml-1 text-xs text-green-700 font-semibold">(Verified Steerer ✓)</span>}</p>
                {selected.warning_lights && <p><strong>Lights/Equipment:</strong> {selected.warning_lights}</p>}
                {/* Clearances */}
                {(selected.has_msic || selected.has_federal_police_clearance || selected.has_defence_clearance || selected.has_turbines_access) && (
                  <p><strong>Clearances:</strong> {[
                    selected.has_msic && 'MSIC',
                    selected.has_federal_police_clearance && 'Federal Police',
                    selected.has_defence_clearance && 'Defence Site',
                    selected.has_turbines_access && 'Turbines',
                  ].filter(Boolean).join(', ')}</p>
                )}
                {selected.mine_sites?.length > 0 && <p><strong>Mine Sites:</strong> {selected.mine_sites.join(', ')}</p>}
                {selected.port_access_details && <p><strong>Port Access:</strong> {selected.port_access_details}</p>}
                {selected.railway_inductions && <p><strong>Railway:</strong> {selected.railway_inductions}</p>}
                {selected.aviation_clearances && <p><strong>Aviation:</strong> {selected.aviation_clearances}</p>}
                {selected.preferred_regions?.length > 0 && <p><strong>Prefers:</strong> {selected.preferred_regions.join(', ')}</p>}
                {selected.notes && <p className="mt-1 italic">&ldquo;{selected.notes}&rdquo;</p>}
              </div>
              <p className="text-xs text-gray-600 mb-3">Pilot details stay private. Send your enquiry via Truckn Send It — we&apos;ll pass it to the pilot.</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Your Name *</Label><Input value={enquiry.name} onChange={e => setEnquiry({...enquiry, name: e.target.value})} data-testid="enq-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={enquiry.email} onChange={e => setEnquiry({...enquiry, email: e.target.value})} data-testid="enq-email" /></div>
                <div><Label>Phone</Label><Input value={enquiry.phone} onChange={e => setEnquiry({...enquiry, phone: e.target.value})} /></div>
                <div><Label>Dates</Label><Input value={enquiry.dates} onChange={e => setEnquiry({...enquiry, dates: e.target.value})} placeholder="e.g. 15-16 Mar" /></div>
                <div className="md:col-span-2"><Label>Route</Label><Input value={enquiry.route} onChange={e => setEnquiry({...enquiry, route: e.target.value})} placeholder="Pickup → Dropoff" /></div>
                <div className="md:col-span-2"><Label>Message</Label><Textarea rows={3} value={enquiry.message} onChange={e => setEnquiry({...enquiry, message: e.target.value})} /></div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={sendEnquiry} disabled={sending} className="bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="send-enquiry-btn">
                  <Send className="w-4 h-4 mr-1" /> {sending ? 'Sending...' : 'Send Enquiry'}
                </Button>
                <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
              </div>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PilotsAvailable;
